import { Box, HStack, VStack } from '@chakra-ui/react'
import ScramblePreview from './ScramblePreview'

interface PLLCaseVisualizerProps {
  pllCase: string
  size?: { width: string; height: string }
}

// Map PLL cases to their setup moves (yellow on top)
const PLL_CASES: Record<string, string> = {
  // Adjacent Corner Swap
  'Aa': "z2 x R' U R' D2 R U' R' D2 R2 x'",  // A perm
  'Ab': "z2 x' R U' R D2 R' U R D2 R2 x",     // A perm
  'F': "z2 R' U' F' R U R' U' R' F R2 U' R' U' R U R' U R",  // F perm
  'Ga': "z2 R2 U R' U R' U' R U' R2 D U' R' U R D'",  // Ga perm
  'Gb': "z2 R' U' R U D' R2 U R' U R U' R U' R2 D",   // Gb perm
  'Gc': "z2 R2 U' R U' R U R' U R2 D' U R U' R' D",   // Gc perm
  'Gd': "z2 R U R' U' D R2 U' R U' R' U R' U R2 D'",  // Gd perm
  'Ja': "z2 x R2 F R F' R U2 r' U r U2 x'",           // Ja perm
  'Jb': "z2 R U R' F' R U R' U' R' F R2 U' R'",       // Jb perm
  'Ra': "z2 R U' R' U' R U R D R' U' R D' R' U2 R'",  // Ra perm
  'Rb': "z2 R2 F R U R U' R' F' R U2 R' U2 R",        // Rb perm
  'T': "z2 R U R' U' R' F R2 U' R' U' R U R' F'",     // T perm
  
  // Diagonal Corner Swap
  'E': "z2 x' R U' R' D R U R' D' R U R' D R U' R' D' x",  // E perm
  'Na': "z2 R U R' U R U R' F' R U R' U' R' F R2 U' R' U2 R U' R'",  // Na perm
  'Nb': "z2 R' U R U' R' F' U' F R U R' F R' F' R U' R",   // Nb perm
  'V': "z2 R' U R' U' y R' F' R2 U' R' U R' F R F",        // V perm
  'Y': "z2 F R U' R' U' R U R' F' R U R' U' R' F R F'",    // Y perm
  
  // Edges Only
  'H': "z2 M2 U M2 U2 M2 U M2",                            // H perm
  'Ua': "z2 M2 U M U2 M' U M2",                            // Ua perm
  'Ub': "z2 M2 U' M U2 M' U' M2",                          // Ub perm
  'Z': "z2 M' U M2 U M2 U M' U2 M2"                        // Z perm
}

// Map PLL cases to their 2D flat view representation
// Each array represents the colors of the edges around the yellow face:
// [top3, right3, bottom3, left3] - each group of 3 represents the edge stickers in order
// Colors based on JPerm.net PLL patterns
const PLL_2D_PATTERNS: Record<string, string[][]> = {
  // Adjacent Corner Swaps
  'Aa': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','g'], // bottom
    ['o','r','b']  // left
  ],
  'Ab': [
    ['g','g','o'], // top
    ['o','o','r'], // right
    ['b','b','g'], // bottom
    ['r','r','b']  // left
  ],
  'F': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Ga': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Gb': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Gc': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Gd': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Ja': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Jb': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Ra': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Rb': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'T': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],

  // Diagonal Corner Swaps
  'E': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Na': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Nb': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'V': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Y': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],

  // Edges Only
  'H': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Ua': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Ub': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ],
  'Z': [
    ['g','g','r'], // top
    ['r','o','o'], // right
    ['b','b','o'], // bottom
    ['o','r','g']  // left
  ]
}

const PLLCaseVisualizer = ({ pllCase, size = { width: '200px', height: '150px' } }: PLLCaseVisualizerProps) => {
  const setupMoves = PLL_CASES[pllCase]
  const pattern = PLL_2D_PATTERNS[pllCase]
  
  if (!setupMoves || !pattern) {
    return null
  }

  // Create SVG for 2D flat view
  const renderFlatView = () => {
    const svgSize = 100
    const cellSize = svgSize / 3
    const borderWidth = cellSize / 3
    
    return (
      <Box width={size.width} height={size.height}>
        <svg
          viewBox={`0 0 ${svgSize + 2 * borderWidth} ${svgSize + 2 * borderWidth}`}
          style={{
            width: '100%',
            height: '100%',
            maxWidth: '200px',
            margin: '0 auto',
          }}
        >
          {/* Background */}
          <rect
            x={borderWidth}
            y={borderWidth}
            width={svgSize}
            height={svgSize}
            fill="#FFD700" // Yellow
            stroke="black"
            strokeWidth="1"
          />

          {/* Grid lines */}
          <g stroke="black" strokeWidth="0.5">
            {/* Vertical lines */}
            <line 
              x1={borderWidth + cellSize} 
              y1={borderWidth} 
              x2={borderWidth + cellSize} 
              y2={borderWidth + svgSize} 
            />
            <line 
              x1={borderWidth + 2 * cellSize} 
              y1={borderWidth} 
              x2={borderWidth + 2 * cellSize} 
              y2={borderWidth + svgSize} 
            />
            {/* Horizontal lines */}
            <line 
              x1={borderWidth} 
              y1={borderWidth + cellSize} 
              x2={borderWidth + svgSize} 
              y2={borderWidth + cellSize} 
            />
            <line 
              x1={borderWidth} 
              y1={borderWidth + 2 * cellSize} 
              x2={borderWidth + svgSize} 
              y2={borderWidth + 2 * cellSize} 
            />
          </g>

          {/* Top edge */}
          {pattern[0].map((color, i) => (
            <rect
              key={`top-${i}`}
              x={borderWidth + i * cellSize}
              y={0}
              width={cellSize}
              height={borderWidth}
              fill={getColor(color)}
              stroke="black"
              strokeWidth="0.5"
            />
          ))}

          {/* Right edge */}
          {pattern[1].map((color, i) => (
            <rect
              key={`right-${i}`}
              x={borderWidth + svgSize}
              y={borderWidth + i * cellSize}
              width={borderWidth}
              height={cellSize}
              fill={getColor(color)}
              stroke="black"
              strokeWidth="0.5"
            />
          ))}

          {/* Bottom edge */}
          {pattern[2].map((color, i) => (
            <rect
              key={`bottom-${i}`}
              x={borderWidth + i * cellSize}
              y={borderWidth + svgSize}
              width={cellSize}
              height={borderWidth}
              fill={getColor(color)}
              stroke="black"
              strokeWidth="0.5"
            />
          ))}

          {/* Left edge */}
          {pattern[3].map((color, i) => (
            <rect
              key={`left-${i}`}
              x={0}
              y={borderWidth + i * cellSize}
              width={borderWidth}
              height={cellSize}
              fill={getColor(color)}
              stroke="black"
              strokeWidth="0.5"
            />
          ))}
        </svg>
      </Box>
    )
  }

  const getColor = (color: string) => {
    switch (color) {
      case 'r': return '#FF0000' // Red
      case 'g': return '#00FF00' // Green
      case 'o': return '#FFA500' // Orange
      case 'b': return '#0000FF' // Blue
      default: return '#FFFFFF'  // White
    }
  }

  return (
    <HStack 
      spacing={4} 
      align="center" 
      justify="center"
      width="100%"
      flexDirection={{ base: 'column', md: 'row' }}
    >
      <Box 
        width={{ base: '100%', md: '50%' }}
        display="flex"
        justifyContent="center"
      >
        <ScramblePreview
          scramble={setupMoves}
          size={{ width: '200px', height: '150px' }}
          visualization="3D"
          event="333"
        />
      </Box>
      <Box 
        width={{ base: '100%', md: '50%' }}
        display="flex"
        justifyContent="center"
      >
        {renderFlatView()}
      </Box>
    </HStack>
  )
}

export default PLLCaseVisualizer 